/**
 * Gmail Auth — client-side Google OAuth via Google Identity Services (GIS).
 * Scope: gmail.readonly only (we only read order confirmations).
 * Token stored in localStorage. Emails never leave the browser.
 */

const STORAGE_KEY = 'susu_gmail_token';
const SCOPES = 'https://www.googleapis.com/auth/gmail.readonly';

// GIS client ID — set via env var
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

if (!CLIENT_ID) {
  console.warn('[gmailAuth] REACT_APP_GOOGLE_CLIENT_ID is not set in .env. Gmail connect will fail.');
}

let tokenClient = null;

/**
 * Load Google Identity Services script dynamically.
 */
function loadGISScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/**
 * Initialize the GIS token client.
 */
async function getTokenClient() {
  if (tokenClient) return tokenClient;

  await loadGISScript();

  return new Promise((resolve) => {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: () => {} // Overridden per-request
    });
    resolve(tokenClient);
  });
}

/**
 * Connect Gmail — opens Google consent, stores token.
 * @returns {Promise<string>} Access token
 */
export async function connectGmail() {
  if (typeof window !== 'undefined' && window.ReactNativeWebView !== undefined) {
    // Native app flow via React Native WebView bridge
    return new Promise((resolve, reject) => {
      const handleMessage = (event) => {
        let data;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch (e) {
          return;
        }

        if (data && data.type === 'GMAIL_CONNECT_SUCCESS') {
          cleanup();
          const tokenData = {
            access_token: data.payload.accessToken,
            expires_at: Date.now() + (3600 * 1000), // Default 1 hour expiry
            scope: SCOPES
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData));
          resolve(data.payload.accessToken);
        } else if (data && data.type === 'GMAIL_CONNECT_ERROR') {
          cleanup();
          reject(new Error(data.payload.error || 'Native Gmail login failed'));
        }
      };

      const cleanup = () => {
        window.removeEventListener('message', handleMessage);
        document.removeEventListener('message', handleMessage);
      };

      window.addEventListener('message', handleMessage);
      document.addEventListener('message', handleMessage); // For some iOS versions

      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CONNECT_GMAIL' }));
    });
  }

  // Web flow via Google Identity Services
  if (!CLIENT_ID) {
    throw new Error('Google Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env');
  }

  const client = await getTokenClient();

  return new Promise((resolve, reject) => {
    client.callback = (response) => {
      if (response.error) {
        reject(new Error(response.error_description || response.error));
        return;
      }
      const tokenData = {
        access_token: response.access_token,
        expires_at: Date.now() + (response.expires_in * 1000),
        scope: response.scope
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokenData));
      resolve(response.access_token);
    };

    client.requestAccessToken({ prompt: 'consent' });
  });
}

/**
 * Get stored Gmail access token (null if expired or not connected).
 */
export function getGmailToken() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    // Check expiration with 60s buffer
    if (data.expires_at && Date.now() > data.expires_at - 60000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data.access_token;
  } catch {
    return null;
  }
}

/**
 * Check if Gmail is connected and token is valid.
 */
export function isGmailConnected() {
  return !!getGmailToken();
}

/**
 * Disconnect Gmail — clear stored token.
 */
export function disconnectGmail() {
  localStorage.removeItem(STORAGE_KEY);
  const token = getGmailToken();
  if (token && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(token);
  }
}
